import { IPageRequest, IPagedResult } from "src/Shared/Interfaces";
import { IRootStore } from "src/Shared/Stores/Root.store";
import { ICompanyOffice } from "../Interfaces/ICompanyOffice";
import { action, makeObservable, observable } from "mobx";
import { Subscription, finalize, forkJoin } from "rxjs";
import CompanyOfficeService from "../Services/CompanyOffice.service";
import { AxiosError } from "axios";
import { IOfficeFilter } from "../Interfaces/IOfficeFilter";
import { ToastService } from "src/Shared/Services";

export class CompanyOfficeStore {
  rootStore: IRootStore;
  public gridLoading: boolean = false;
  public primaryOfficeID: number | null = null;

  // The request the grid is currently showing. A refresh after a save reuses it so
  // the user stays on the same page/page size — refetching a hard-coded page 1 swaps
  // every visible row and reads like the whole page reloaded.
  private lastRequest: IPageRequest<IOfficeFilter> | null = null;

  public companyOfficeList: IPagedResult<ICompanyOffice> = {
    pageNumber: 1,
    pageSize: 10,
    results: [],
    totalNumberOfRecords: 0,
  };

  constructor(rootStore: IRootStore) {
    makeObservable(this, {
      gridLoading: observable,
      companyOfficeList: observable,
      setGridLoading: action,
      getCompanyOffices: action.bound,
      setCompanyOfficesList: action,
      refreshCompanyOfficeList: action,
    });
    this.rootStore = rootStore;
  }

  setGridLoading = (flag: boolean): void => {
    this.gridLoading = flag;
  };

  // silent = the existing app pattern for refreshes after a save: rows update
  // in place with no grid loading overlay, so only the changed content repaints.
  getCompanyOffices(
    request: IPageRequest<IOfficeFilter>,
    silent: boolean = false
  ): Subscription {
    this.lastRequest = request;
    if (!silent) this.setGridLoading(true);
    return forkJoin([
      CompanyOfficeService.getCompanyOfficesList(request),
      CompanyOfficeService.getPrimaryOfficeID(
        request.filterCollection.companyRowID
      ),
    ])
      .pipe(
        finalize(() => {
          if (!silent) this.setGridLoading(false);
        })
      )
      .subscribe({
        next: ([listdata, primaryOfficeID]) => {
          this.setCompanyOfficesList(listdata);
          this.setprimaryOfficeID(primaryOfficeID);
        },
        error: (error: AxiosError) =>  ToastService.axiosError(error),
      });
  }

  setCompanyOfficesList(data: IPagedResult<ICompanyOffice>) {
    if (data) {
      this.companyOfficeList = data;
    }
  }

  setprimaryOfficeID(val: number) {
    this.primaryOfficeID = val ? val : null;
  }

  refreshCompanyOfficeList = (companyRowID: string, silent: boolean = false) => {
    const sameCompany =
      this.lastRequest?.filterCollection?.companyRowID === companyRowID;

    this.getCompanyOffices(
      sameCompany
        ? this.lastRequest
        : {
            pageNumber: 1,
            pageSize: 10,
            filterCollection: {
              companyRowID: companyRowID,
            },
            sortCollection: [],
          },
      silent
    );
  };

  resetCompanyList = () => {
    const companyOfficeList = { ...this.companyOfficeList };
    companyOfficeList.results = [];
    this.setCompanyOfficesList(companyOfficeList);
  };
}
